import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";

// GSM AI Scoring Verification Wizard — Stage 4 picker UI.
//
// Runs entirely against the dedicated, isolated "GSM Wizard Sandbox"
// society on the server — never a real club/society's data. Players and
// the course are fictitious test fixtures that already exist in that
// sandbox; this page only lets you pick a format and a simulation count,
// not real people.
//
// Gated by its own access key (X-Wizard-Key), checked server-side on
// every call — this page never assumes the key it holds is valid, it
// just remembers the last one typed and lets the server be the judge.

const FORMATS = [
  { value: "singles-stableford", label: "Singles Stableford", playerCount: true, hasBatch: true },
  { value: "singles-stableford-full-handicap", label: "Singles Stableford (Full Handicap)", playerCount: true, hasBatch: false },
  { value: "better-ball-stableford", label: "Better Ball Stableford", playerCount: false, hasBatch: true },
  { value: "fourball-better-ball-stableford", label: "Fourball Better Ball Stableford", playerCount: false, hasBatch: false },
  { value: "foursomes-stableford", label: "Foursomes Stableford", playerCount: false, hasBatch: true },
  { value: "greensomes-stableford", label: "Greensomes Stableford", playerCount: false, hasBatch: true },
  { value: "texas-scramble-4", route: "texas-scramble", label: "Texas Scramble (4 players)", fixedGroupSize: 4, hasBatch: false },
  { value: "texas-scramble-3", route: "texas-scramble", label: "Texas Scramble (3 players)", fixedGroupSize: 3, hasBatch: false },
  { value: "team-best2of4-stableford", label: "Team Stableford (Best 2 of 4)", hasBatch: false },
  { value: "greensomes-medal", label: "Greensomes Medal", playerCount: false, hasBatch: true },
  { value: "skins", label: "Skins", playerCount: false, hasBatch: true },
  { value: "foursomes-matchplay", label: "Foursomes Match Play", playerCount: false, hasBatch: true },
  { value: "greensomes-matchplay", label: "Greensomes Match Play", playerCount: false, hasBatch: true },
  { value: "fourball-matchplay", label: "Fourball Match Play", playerCount: false, hasBatch: true },
  { value: "singles-medal", label: "Singles Medal", playerCount: true, hasBatch: true },
  { value: "singles-strokeplay", label: "Singles Stroke Play", playerCount: true, hasBatch: true },
  { value: "singles-matchplay", label: "Singles Match Play", playerCount: false, hasBatch: true },
  { value: "better-ball-medal", label: "Better Ball Medal", playerCount: false, hasBatch: true },
  { value: "better-ball-strokeplay", label: "Better Ball Stroke Play", playerCount: false, hasBatch: true },
  { value: "fourball-strokeplay", label: "Fourball Stroke Play", playerCount: false, hasBatch: true },
  { value: "foursomes-medal", label: "Foursomes Medal", playerCount: false, hasBatch: true },
  { value: "texas-scramble-medal-4", route: "texas-scramble-medal", label: "Texas Scramble Medal (4 players)", fixedGroupSize: 4, hasBatch: true },
  { value: "texas-scramble-medal-3", route: "texas-scramble-medal", label: "Texas Scramble Medal (3 players)", fixedGroupSize: 3, hasBatch: true },
  { value: "team-stableford", label: "Team Stableford", playerCount: false, hasBatch: true },
  { value: "team-medal", label: "Team Medal", playerCount: false, hasBatch: true },
  { value: "team-aggregate-stableford", label: "Team Aggregate Stableford", playerCount: false, hasBatch: true },
  { value: "team-aggregate-strokeplay", label: "Team Aggregate Stroke Play", playerCount: false, hasBatch: true },
  { value: "better-ball-matchplay", label: "Better Ball Match Play", playerCount: false, hasBatch: true },
  { value: "team-matchplay", label: "Team Match Play", playerCount: false, hasBatch: true },
];

const SIM_COUNTS = [1, 10, 100];

// The wizard key must never outlive the login session it was entered
// under -- confirmed as a real bug 2026-10-25: a different organiser
// (ben42@aol) logged in on a browser where a previous account had
// already unlocked the Wizard, and localStorage's flat "wizard_key"
// silently let them straight through with no prompt at all, since it
// has no concept of WHICH account unlocked it. Tying the stored key to
// the exact auth token present when it was entered means a fresh login
// (any account, including re-logging in as the same one) always starts
// locked again.
function currentAuthToken() {
  try { return sessionStorage.getItem("token") || ""; } catch (e) { return ""; }
}
function loadStoredKey() {
  try {
    const storedForToken = localStorage.getItem("wizard_key_for_token") || "";
    if (!storedForToken || storedForToken !== currentAuthToken()) return "";
    return localStorage.getItem("wizard_key") || "";
  } catch (e) { return ""; }
}
function storeKey(key) {
  try {
    localStorage.setItem("wizard_key", key);
    localStorage.setItem("wizard_key_for_token", currentAuthToken());
  } catch (e) {}
}
function clearStoredKey() {
  try { localStorage.removeItem("wizard_key"); localStorage.removeItem("wizard_key_for_token"); } catch (e) {}
}

function VerdictBadge({ verdict }) {
  const styles = {
    PASS: "bg-green-100 text-green-800 border-green-300",
    FAIL: "bg-red-100 text-red-800 border-red-300",
    KNOWN_LIMITATION: "bg-amber-100 text-amber-800 border-amber-300",
    NOT_VERIFIED: "bg-gray-100 text-gray-700 border-gray-300",
  };
  const text = { PASS: "PASS", FAIL: "FAIL", KNOWN_LIMITATION: "KNOWN LIMITATION", NOT_VERIFIED: "NOT VERIFIED" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold ${styles[verdict] || styles.NOT_VERIFIED}`}>
      {text[verdict] || verdict}
    </span>
  );
}

function CheckpointTable({ entries, idLabel, format, explanations, explainingKey, onExplain }) {
  const fieldLabels = { points: "Points", gross: "Gross", net: "Net", back9: "Back 9", last6: "Last 6", last3: "Last 3", last1: "Last 1", thru: "Thru", position: "Position", margin: "Margin", winningSide: "Winning Side", isComplete: "Complete" };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-3">{idLabel}</th>
            <th className="py-2 pr-3">Checkpoint</th>
            <th className="py-2 pr-3">GSM (real app)</th>
            <th className="py-2 pr-3">Independent engine</th>
            <th className="py-2 pr-3">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, ei) => {
            const checks = entry.checks || {};
            const rows = Object.keys(checks);
            return rows.map((field, fi) => (
              <tr key={ei + "-" + field} className="border-b border-gray-100">
                {fi === 0 && (
                  <td rowSpan={rows.length + (entry.verdict === "FAIL" ? 1 : 0)} className="py-2 pr-3 align-top font-medium text-gray-800">
                    {entry.name || entry.names}
                    <div className="mt-1"><VerdictBadge verdict={entry.verdict} /></div>
                  </td>
                )}
                <td className="py-2 pr-3 text-gray-600">{fieldLabels[field] || field}</td>
                <td className="py-2 pr-3 font-mono">{String(checks[field].a)}</td>
                <td className="py-2 pr-3 font-mono">{String(checks[field].b)}</td>
                <td className="py-2 pr-3"><VerdictBadge verdict={checks[field].verdict} /></td>
              </tr>
            )).concat(entry.verdict === "FAIL" ? [(() => {
              const key = ei + "-explain";
              const explanation = explanations && explanations[key];
              const isExplaining = explainingKey === key;
              return (
                <tr key={key} className="border-b border-gray-100 bg-purple-50">
                  <td colSpan={4} className="py-2 pr-3 align-top">
                    {explanation ? (
                      <div className="text-xs text-purple-900 whitespace-pre-wrap">🤖 {explanation}</div>
                    ) : (
                      <button type="button" disabled={isExplaining}
                        onClick={() => onExplain && onExplain(key, format, entry)}
                        className="text-xs text-purple-700 underline disabled:text-gray-400">
                        {isExplaining ? "Asking Claude..." : "🤖 Explain with AI"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })()] : []);
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Wizard() {
  const [keyInput, setKeyInput] = useState("");
  const [unlocked, setUnlocked] = useState(!!loadStoredKey());
  const [keyError, setKeyError] = useState("");

  const [format, setFormat] = useState(FORMATS[0].value);
  const [simCount, setSimCount] = useState(1);
  const [playerCount, setPlayerCount] = useState(6);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [explanations, setExplanations] = useState({});
  const [explainingKey, setExplainingKey] = useState(null);

  const explainFailure = async (key, fmt, entry) => {
    setExplainingKey(key);
    try {
      const res = await api.post("/wizard/explain", { format: fmt, checks: entry.checks, component_a: entry.component_a, component_b: entry.component_b }, { headers: { "X-Wizard-Key": loadStoredKey() } });
      setExplanations(prev => ({ ...prev, [key]: res.data.explanation }));
    } catch (e) {
      setExplanations(prev => ({ ...prev, [key]: "Could not get an explanation: " + (e.response?.data?.error || e.message) }));
    } finally {
      setExplainingKey(null);
    }
  };

  const currentFormat = FORMATS.find(f => f.value === format) || FORMATS[0];

  const unlock = () => {
    if (!keyInput.trim()) return;
    storeKey(keyInput.trim());
    setUnlocked(true);
    setKeyError("");
  };

  const run = async () => {
    setRunning(true);
    setError("");
    setResult(null);
    const key = loadStoredKey();
    const isBatch = simCount > 1 && currentFormat.hasBatch;
    const routeName = currentFormat.route || format;
    const path = `/wizard/${routeName}/${isBatch ? "batch" : "run"}`;
    const body = {
      ...(isBatch ? { simulations: simCount } : {}),
      ...(currentFormat.playerCount ? { playerCount } : {}),
      ...(currentFormat.fixedGroupSize ? { groupSize: currentFormat.fixedGroupSize } : {}),
    };
    try {
      const res = await api.post(path, body, { headers: { "X-Wizard-Key": key } });
      setResult(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        clearStoredKey();
        setUnlocked(false);
        setKeyError("That key was rejected by the server. Enter the correct Wizard access key.");
      } else {
        setError(e.response?.data?.error || e.message || "Something went wrong");
      }
    } finally {
      setRunning(false);
    }
  };

  const exportReport = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `wizard-report-${format}-${Date.now()}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  if (!unlocked) {
    return (
      <Layout>
        <div className="p-6 max-w-md">
          <h1 className="text-2xl font-bold text-green-700 mb-1">🧙 AI Scoring Verification Wizard</h1>
          <p className="text-sm text-gray-500 mb-6">Enter the Wizard access key to continue.</p>
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <input
              type="password"
              value={keyInput}
              onChange={e => setKeyInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && unlock()}
              placeholder="Wizard access key"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            {keyError && <p className="text-sm text-red-600 mb-3">{keyError}</p>}
            <button onClick={unlock} className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2 rounded-lg">
              Unlock
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const isBatchResult = result && Array.isArray(result.runs);
  const displayedRuns = isBatchResult && showErrorsOnly ? result.runs.filter(r => r.verdict === "FAIL") : (isBatchResult ? result.runs : null);

  return (
    <Layout>
      <div className="p-6 max-w-4xl">
        <h1 className="text-2xl font-bold text-green-700 mb-1">🧙 AI Scoring Verification Wizard</h1>
        <p className="text-sm text-gray-500 mb-6">
          Runs entirely against the dedicated test sandbox — fictitious players, a fictitious course, never real society data.
        </p>

        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Format</label>
              <select value={format} onChange={e => setFormat(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            {currentFormat.playerCount && (
              <div>
                <label className="block text-sm text-gray-700 mb-1">Players per simulation</label>
                <input type="number" min={2} max={8} value={playerCount} onChange={e => setPlayerCount(parseInt(e.target.value) || 2)}
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            )}
            {currentFormat.hasBatch && (
              <div>
                <label className="block text-sm text-gray-700 mb-1">Simulations</label>
                <select value={simCount} onChange={e => setSimCount(parseInt(e.target.value))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {SIM_COUNTS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <button onClick={run} disabled={running}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2 rounded-lg">
              {running ? "Running…" : "Generate & Verify"}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-6">{error}</div>
        )}

        {result && !isBatchResult && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Result</h2>
              <div className="flex items-center gap-3">
                <VerdictBadge verdict={result.verdict} />
                <button onClick={exportReport} className="text-sm text-green-700 underline">Export Report</button>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-3">Course: Hagley Golf And Country Club (fixed sandbox course, same for every simulation -- only the players, handicaps and scores are random)</p>
            <CheckpointTable
              entries={result.players || result.pairs || result.groups || (result.checks ? [{ name: "Match", verdict: result.verdict, checks: result.checks, component_a: result.component_a, component_b: result.component_b }] : [])}
              idLabel={result.players ? "Player" : (result.groups ? "Group" : (result.pairs ? "Pair" : "Match"))}
              format={currentFormat.label} explanations={explanations} explainingKey={explainingKey} onExplain={explainFailure}
            />
            {result.cleanupError && <p className="text-xs text-red-500 mt-3">Cleanup warning: {result.cleanupError}</p>}
          </div>
        )}

        {isBatchResult && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Batch Result</h2>
              <button onClick={exportReport} className="text-sm text-green-700 underline">Export Report</button>
            </div>
            <p className="text-xs text-gray-400 mb-3">Course: Hagley Golf And Country Club (fixed sandbox course, same for every simulation -- only the players, handicaps and scores are random)</p>
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-800">{result.total}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-700">{result.passed}</div>
                <div className="text-xs text-gray-500">Passed</div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-red-700">{result.failed}</div>
                <div className="text-xs text-gray-500">Failed</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-gray-800">{result.accuracy}%</div>
                <div className="text-xs text-gray-500">Accuracy</div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <input type="checkbox" checked={showErrorsOnly} onChange={e => setShowErrorsOnly(e.target.checked)} />
              View Errors only
            </label>
            {displayedRuns.map((run, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-3 mb-2">
                <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
                  <span>Run {i + 1}</span>
                  <VerdictBadge verdict={run.verdict} />
                </div>
                {Array.isArray(run.players) && <CheckpointTable entries={run.players} idLabel="Player" format={currentFormat.label} explanations={explanations} explainingKey={explainingKey} onExplain={explainFailure} />}
                {Array.isArray(run.pairs) && <CheckpointTable entries={run.pairs} idLabel="Pair" format={currentFormat.label} explanations={explanations} explainingKey={explainingKey} onExplain={explainFailure} />}
                {Array.isArray(run.groups) && <CheckpointTable entries={run.groups} idLabel="Group" format={currentFormat.label} explanations={explanations} explainingKey={explainingKey} onExplain={explainFailure} />}
                {!run.players && !run.pairs && !run.groups && run.checks && <CheckpointTable entries={[{ name: "Match", verdict: run.verdict, checks: run.checks, component_a: run.component_a, component_b: run.component_b }]} idLabel="Match" format={currentFormat.label} explanations={explanations} explainingKey={explainingKey} onExplain={explainFailure} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
